import {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

// UseCase
import {
  createValidationSchema,
  getNonNullFilterWithType,
  isMultiValueType,
} from './conditionalFiltersUseCase';

// Utils
import groupOptionsByOptGroup from '../../utils/optGroup/optGroup';

/**
 * @typedef {object} useConditionalFiltersReturn
 * @property {ConditionalFilter[]} tableFilters - Table filters
 * @property {Array<{lib: string, options: Array<ConditionalFilter>}>} tableFiltersGrouped - Table filters grouped by optGroup
 * @property {import('react-hook-form').UseFormReturn<{ conditionalFilters: ApiManagerGlobalFiltersCreateValue[] }>} formConditionalFilters
 * @property {Record<string, ConditionalFilter>} conditionalFiltersById
 * @property {ApiManagerGlobalFiltersCreateValue[]} conditionalFiltersFields
 * @property {() => void} handleAddConditionalFilter
 * @property {(index: number) => void} handleRemoveConditionalFilter
 * @property {(index: number, next: ApiManagerGlobalFiltersCreateValue) => void} handleUpdateConditionalFilterType
 * @property {(index: number, next: ApiManagerGlobalFiltersCreateValue) => void} handleUpdateConditionalFilterOperator
 * @property {() => void} handleClearAllConditionalFilters
 * @property {() => void} handleCancelConditionalFilters
 * @property {boolean} popoverConditionalFiltersIsOpen
 * @property {(isOpen: boolean) => void} setPopoverConditionalFiltersIsOpen
 * @property {(fieldsArray: Array<ApiManagerFilterValues>) => number} getConditionalFiltersCount
 * @property {number | null} criterionSelectFocusIndex - Row index whose criterion select should receive focus
 * @property {() => void} clearCriterionSelectFocus - Clear focus target after focusing
 */

/**
 * Hook to manage the conditional filters popover
 * @param {object} props
 * @param {Array<ApiManagerGlobalFiltersFilter>} props.filters
 * @param {ApiManagerGlobalFiltersCreateValue[]} [props.initialValues]
 * @param {boolean} props.isLoading
 * @returns {useConditionalFiltersReturn} The conditional filters popover state and actions
 */
const useConditionalFilters = ({ filters, initialValues, isLoading }) => {
  const [popoverConditionalFiltersIsOpen, setPopoverConditionalFiltersIsOpen] = useState(false);
  const [criterionSelectFocusIndex, setCriterionSelectFocusIndex] = useState(
    /** @type {number | null} */ (null),
  );

  /**
   * Calculate tableFilters and tableFiltersGrouped in a single useMemo
   * @type {{ tableFilters: ConditionalFilter[], tableFiltersGrouped: Array<{lib: string, options: Array<ConditionalFilter>}> }}
   */
  const { tableFilters, tableFiltersGrouped } = useMemo(() => {
    if (isLoading || !filters) {
      return {
        tableFilters: [],
        tableFiltersGrouped: [],
      };
    }

    const computedTableFilters = filters.reduce(
      (acc, filter) => {
        const nonNullFilter = getNonNullFilterWithType(filter);
        return nonNullFilter ? [...acc, nonNullFilter] : acc;
      },
      /** @type {ConditionalFilter[]} */ ([]),
    );

    return {
      tableFilters: computedTableFilters,
      tableFiltersGrouped: groupOptionsByOptGroup(computedTableFilters),
    };
  }, [filters, isLoading]);

  /**
   * @type {Record<string, ConditionalFilter>}
   */
  const conditionalFiltersById = useMemo(() => (isLoading || !tableFilters ? {} : tableFilters.reduce(
    (acc, filter) => {
      acc[filter.id] = filter;
      return acc;
    },
    /** @type {Record<string, ConditionalFilter>} */ ({}),
  )), [tableFilters, isLoading]);

  // Form for conditional filters
  /** @type {ApiManagerGlobalFiltersCreateValue} */
  const conditionalFiltersInitial = {
    id: '',
    operator: '',
    values: [],
    singleValue: true,
  };

  /** @type {import('react-hook-form').UseFormReturn<{ conditionalFilters: ApiManagerGlobalFiltersCreateValue[] }>} */
  const formConditionalFilters = useForm({
    defaultValues: {
      conditionalFilters: initialValues && initialValues.length > 0 ? initialValues : [conditionalFiltersInitial],
    },
    resolver: zodResolver(createValidationSchema(conditionalFiltersById)),
    mode: 'onChange',
  });

  /**
   * UseEffect to reset the form with the initial values
   */
  useEffect(() => {
    if (initialValues && initialValues.length > 0 && !isLoading) {
      formConditionalFilters.reset({ conditionalFilters: initialValues });
    }
  }, [initialValues, isLoading]);

  const { watch, reset, control } = formConditionalFilters;
  const watchedConditionalFilters = watch('conditionalFilters');

  const {
    fields /** @type {Array<ApiManagerFilterValues>} */,
    append,
    remove,
    update,
  } = useFieldArray({
    control,
    name: 'conditionalFilters',
    keyName: '_id',
  });

  /**
   * Handle add conditional filter
   */
  const handleAddConditionalFilter = () => {
    // If the first filter is empty, reset the form with the initial values
    if (watchedConditionalFilters?.length && !watchedConditionalFilters[0].id) {
      setCriterionSelectFocusIndex(0);
      reset({ conditionalFilters: [conditionalFiltersInitial] });
      return;
    }
    const newIndex = watchedConditionalFilters?.length ?? 0;
    setCriterionSelectFocusIndex(newIndex);
    append(conditionalFiltersInitial);
  };

  const clearCriterionSelectFocus = useCallback(() => {
    setCriterionSelectFocusIndex(null);
  }, []);

  /**
   * Handle clear all conditional filters
   */
  const handleClearAllConditionalFilters = () => {
    reset({ conditionalFilters: [{}] });
  };

  /**
   * Handle remove conditional filter
   * @param {number} index
   */
  const handleRemoveConditionalFilter = (index) => {
    remove(index);
    if (watchedConditionalFilters.length <= 1) {
      handleClearAllConditionalFilters();
    }
  };

  /**
   * Handle update conditional filter type
   * @param {number} index
   * @param {ApiManagerGlobalFiltersCreateValue} next
   */
  const handleUpdateConditionalFilterType = (index, next) => {
    const field = watchedConditionalFilters[index];
    if (next.id === field.id) return;
    const filterType = conditionalFiltersById[next.id]?.type;

    // Get the first operator from operatorOptions if available to select it automatically
    const operatorOptions = conditionalFiltersById[next.id]?.operatorOptions;
    const defaultOperator = operatorOptions && operatorOptions.length > 0
      ? operatorOptions[0]
      : '';

    /** @type {ApiManagerGlobalFiltersCreateValue} */
    const newValue = {
      ...field,
      id: next.id,
      operator: /** @type {ApiManagerGlobalFiltersOperator} */ (defaultOperator),
      values: [],
      singleValue: !isMultiValueType(filterType),
    };
    update(index, newValue);

    formConditionalFilters.clearErrors(`conditionalFilters.${index}.operator`);
    formConditionalFilters.clearErrors(`conditionalFilters.${index}.values`);
  };

  /**
   * Handle update conditional filter
   * @param {number} index
   * @param {ApiManagerGlobalFiltersCreateValue} next
   */
  const handleUpdateConditionalFilterOperator = (index, next) => {
    const field = watchedConditionalFilters[index];
    if (next.operator === field.operator) return;
    const filterType = conditionalFiltersById[next.id]?.type;

    /** @type {ApiManagerGlobalFiltersCreateValue} */
    const newValue = {
      ...field,
      id: next.id,
      values: [],
      singleValue: !isMultiValueType(filterType),
    };
    update(index, newValue);

    formConditionalFilters.clearErrors(`conditionalFilters.${index}.values`);
  };

  /**
   * Handle cancel conditional filters
   */
  const handleCancelConditionalFilters = () => {
    setPopoverConditionalFiltersIsOpen(false);
    reset({ conditionalFilters: initialValues });
  };

  /**
   * Get the number of conditional filters
   * @param {Array<ApiManagerFilterValues>} fieldsArray
   * @returns {number}
   */
  const getConditionalFiltersCount = useCallback((/** @type {Array<ApiManagerFilterValues>} */ fieldsArray) => (
    fieldsArray?.length ? fieldsArray.filter(
      (/** @type {ApiManagerGlobalFiltersCreateValue} */ filter) => !!filter.id,
    ).length : 0), []);

  return {
    tableFilters,
    tableFiltersGrouped,
    conditionalFiltersById,
    formConditionalFilters,
    conditionalFiltersFields: fields,
    handleAddConditionalFilter,
    handleRemoveConditionalFilter,
    handleUpdateConditionalFilterType,
    handleUpdateConditionalFilterOperator,
    handleClearAllConditionalFilters,
    handleCancelConditionalFilters,

    // Popover
    popoverConditionalFiltersIsOpen,
    setPopoverConditionalFiltersIsOpen,

    // Count
    getConditionalFiltersCount,

    criterionSelectFocusIndex,
    clearCriterionSelectFocus,
  };
};

export default useConditionalFilters;
